import React, { useEffect, useState } from "react";
import { NotionRenderer } from "react-notion-x";
import { ExtendedRecordMap } from "notion-types";
import ky from "ky";
import { useParams, Link } from "react-router-dom";
import _ from "lodash";
import clsx from "clsx";
import { config } from "../services/config";
import { Header } from "../components/Header";
import { Prose } from "../components/Prose";
import { EditHeader } from "../components/EditHeader";
import toast from "react-hot-toast";

export const DOMAIN = config.REACT_APP_VERCEL_URL;
const BACKEND_URL = config.REACT_APP_BACKEND_URL;

interface Props {
  editable?: boolean;
}

interface CacheResponse {
  pageContents: string;
  index: string;
  hierarchy: PageNode;
}

interface PageNode {
  pageId: string;
  children: PageNode[];
  title: string;
}

function SidebarLink({
  generateUrl,
  pageNode,
  currentPageId,
  isTopLevel = false,
}: {
  generateUrl: (pageId: string) => string;
  pageNode: PageNode;
  currentPageId?: string;
  isTopLevel?: boolean;
}) {
  const { pageId, title, children } = pageNode;
  return (
    <div
      className={clsx("ml-2 text-sm mt-2 text-slate-700 hover:text-slate-900")}
    >
      {/* <span
        className={clsx({
          "text-sky-500": currentPageId === pageId,
          "font-bold": isTopLevel,
        })}
      >
        <Link to={generateUrl(pageId)}>{title}</Link>
      </span> */}
      <h2>
        <Link
          to={generateUrl(pageId)}
          className={clsx(
            "block w-full pl-3.5 before:pointer-events-none before:absolute",
            "before:-left-1 before:top-1/2 before:h-1.5 before:w-1.5 before:-translate-y-1/2 before:rounded-full",
            currentPageId === pageId
              ? "font-semibold text-sky-500 before:bg-sky-500"
              : "text-slate-500 before:hidden before:bg-slate-300 hover:text-slate-600 hover:before:block dark:text-slate-400 dark:before:bg-slate-700 dark:hover:text-slate-300",
            isTopLevel ? "font-bold" : null
          )}
        >
          {title}
        </Link>
      </h2>
      {children.map((child) => (
        <SidebarLink
          key={child.pageId}
          generateUrl={generateUrl}
          pageNode={child}
          currentPageId={currentPageId}
        />
      ))}
    </div>
  );
}

export function NotionPage({ editable = false }: Props) {
  const { siteHash, pageId } = useParams();

  const [recordMap, setRecordMap] = useState<ExtendedRecordMap>();
  const [index, setIndex] = useState<string>();
  const [hierarchy, setHierarchy] = useState<PageNode>();

  const fetchPageData = async (siteHash?: string, pageId?: string) => {
    if (!siteHash) {
      return;
    }

    const pageIdQueryString = pageId ? `&pageId=${pageId}` : "";
    const res = await ky.get(
      `${BACKEND_URL}/cache?siteHash=${siteHash}${pageIdQueryString}`
    );
    const cacheResponse: CacheResponse = await res.json();
    const pageContents: ExtendedRecordMap = JSON.parse(
      cacheResponse.pageContents
    );
    setRecordMap(pageContents);
    setIndex(cacheResponse.index);
    setHierarchy(cacheResponse.hierarchy);
  };

  useEffect(() => {
    fetchPageData(siteHash, pageId);
  }, [siteHash, pageId]);

  const refresh = async () => {
    // TODO: Log the errors
    await ky.get(`${BACKEND_URL}/generate?url=${index}`);
    fetchPageData(siteHash, pageId);
  };

  const blockMap = recordMap?.block;
  const toc =
    blockMap &&
    _.filter(blockMap, (b) =>
      ["header", "sub_header", "sub_sub_header"].includes(b.value?.type)
    ).flatMap((h) => ({
      id: h.value.id,
      text: h.value.properties.title[0],
      type: h.value.type,
    }));

  const generateUrlForPage = (pageId: string): string => {
    return editable
      ? `/site/edit/${siteHash}/${pageId}/`
      : `/site/${siteHash}/${pageId}`;
  };

  const generateUrl = (isEditable: boolean, pageId?: string): string => {
    if (isEditable) {
      if (pageId) {
        return `/site/edit/${siteHash}/${pageId}/`;
      } else {
        return `/site/edit/${siteHash}/`;
      }
    } else {
      return !pageId ? `/site/${siteHash}` : `/site/${siteHash}/${pageId}`;
    }
  };

  return recordMap ? (
    <div>
      {editable && (
        <EditHeader
          path={generateUrl(false, pageId)}
          editPath={generateUrl(true, pageId)}
          refresh={() =>
            toast.promise(refresh(), {
              loading: "Syncing...",
              success: <b>Yay! It's updated.</b>,
              error: (err) => (
                <b>
                  We ran into some issues: <br /> {err.toString()}
                </b>
              ),
            })
          }
        />
      )}
      <Header />

      <div className="flex font-[Inter] max-w-8xl sm:px-2 lg:px-8 xl:px-12">
        {/* mx-auto justify-center <div className="relative " */}
        {/* <div className="w-1/5 mt-14 ml-6">
          {hierarchy && siteHash && (
            <SidebarLink
              generateUrl={generateUrlForPage}
              pageNode={hierarchy}
              currentPageId={pageId}
              isTopLevel
            />
          )}
        </div> */}
        <div className="hidden lg:relative lg:block lg:flex-none">
          <div className="absolute inset-y-0 right-0 w-[50vw] bg-slate-50 dark:hidden" />
          <div className="absolute top-16 bottom-0 right-0 hidden h-12 w-px bg-gradient-to-t from-slate-800 dark:block" />
          <div className="absolute top-28 bottom-0 right-0 hidden w-px bg-slate-800 dark:block" />
          <div className="sticky top-[4.5rem] -ml-0.5 h-[calc(100vh-4.5rem)] overflow-y-auto overflow-x-hidden py-16 pl-0.5">
            <nav className="w-64 pr-8 xl:w-72 xl:pr-16">
              {hierarchy && siteHash && (
                <SidebarLink
                  generateUrl={generateUrlForPage}
                  pageNode={hierarchy}
                  currentPageId={pageId}
                  isTopLevel
                />
              )}
            </nav>
          </div>
        </div>
        <div className="min-w-0 max-w-2xl flex-auto px-4 py-16 lg:max-w-none lg:pr-0 lg:pl-8 xl:px-16">
          <Prose className="">
            <NotionRenderer
              recordMap={recordMap}
              mapPageUrl={generateUrlForPage}
            />
          </Prose>
        </div>
        <div className="hidden xl:sticky xl:top-[4.5rem] xl:-mr-6 xl:block xl:h-[calc(100vh-4.5rem)] xl:flex-none xl:overflow-y-auto xl:py-16 xl:pr-6">
          <nav aria-labelledby="on-this-page-title" className="w-56">
            <h2
              id="on-this-page-title"
              className="font-display text-sm font-medium text-slate-900 dark:text-white"
            >
              On this page
            </h2>
            <div className="mt-4 space-y-3 text-sm flex flex-col">
              {toc &&
                toc.map(({ id, text, type }) => (
                  <a
                    className={clsx(
                      "font-normal text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300",
                      [
                        type === "sub_header" && "ml-4",
                        type === "sub_sub_header" && "ml-8",
                      ]
                    )}
                    // TODO: Figure out the active section right now
                    // isActive(section)
                    // ? 'text-sky-500'
                    key={id}
                    href={`#${id.replaceAll("-", "")}`}
                  >
                    {text}
                  </a>
                ))}
            </div>
          </nav>
        </div>
      </div>
    </div>
  ) : (
    <div>No page found</div>
  );
}
